import api from './api';
import { getMonthName } from './dashboardData';

import {
  Text,
  TextInput,
  Select,
  Table,
  Badge,
  Paper,
  Box,
  Flex,
  Group,
  Pagination,
  ActionIcon,
  Switch,
  Button,
} from '@mantine/core';

import {
  IconSearch,
  IconFilter,
  IconArrowUp,
  IconArrowDown,
  IconPlane,
  IconClock,
  IconDownload,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconCloudDownload,
} from '@tabler/icons-react';

import { useState, useMemo, useEffect } from 'react';
import React from 'react';

import * as XLSX from 'xlsx';

const LAST_UPLOAD = new Date().toLocaleString('es-MX', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const MESES = [
  { value: '1', label: 'ENERO' },
  { value: '2', label: 'FEBRERO' },
  { value: '3', label: 'MARZO' },
  { value: '4', label: 'ABRIL' },
  { value: '5', label: 'MAYO' },
  { value: '6', label: 'JUNIO' },
  { value: '7', label: 'JULIO' },
  { value: '8', label: 'AGOSTO' },
  { value: '9', label: 'SEPTIEMBRE' },
  { value: '10', label: 'OCTUBRE' },
  { value: '11', label: 'NOVIEMBRE' },
  { value: '12', label: 'DICIEMBRE' },
];

export default function CapTd() {
  // ============================================================
  // PERIODO
  // ============================================================

  const now = new Date();

  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());

  const prevMonth = () => {
    if (mes === 1) {
      setMes(12);
      setAnio(anio - 1);
    } else {
      setMes(mes - 1);
    }

    setPage(1);
  };

  const nextMonth = () => {
    if (mes === 12) {
      setMes(1);
      setAnio(anio + 1);
    } else {
      setMes(mes + 1);
    }

    setPage(1);
  };

  // ============================================================
  // DATA
  // ============================================================

  const [flightData, setFlightData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        const res = await api.get(
          `/mdi/resumen?mes=${mes}&anio=${anio}&limit=5000`
        );

        setFlightData(res.data?.data || []);
      } catch (e) {
        console.warn('API error:', e.message);
        setFlightData([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [mes, anio]);

  // ============================================================
  // FILTROS
  // ============================================================

  const [search, setSearch] = useState('');
  const [rutaFilter, setRutaFilter] = useState(null);
  const [eqFilter, setEqFilter] = useState(null);
  const [frecFilter, setFrecFilter] = useState(null);
  const [horaFilter, setHoraFilter] = useState(null);

  const [groupByEq, setGroupByEq] = useState(false);
  const [groupByVuelo, setGroupByVuelo] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // ============================================================
  // SORT
  // ============================================================

  const [sortField, setSortField] = useState('ID');
  const [sortDir, setSortDir] = useState('asc');

  // ============================================================
  // PAGINATION
  // ============================================================

  const [page, setPage] = useState(1);
  const perPage = 20;

  // ============================================================
  // OPCIONES DE FILTROS
  // ============================================================

  const horaOptions = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => {
      const h = i.toString().padStart(2, '0');

      return {
        value: h,
        label: `${h}:00 – ${h}:59`,
      };
    });
  }, []);

  const rutaOptions = useMemo(() => {
    const rutas = [
      ...new Set(
        flightData
          .map((d) => d.Ruta)
          .filter(Boolean)
      ),
    ];

    return rutas
      .sort()
      .map((r) => ({
        value: r,
        label: r,
      }));
  }, [flightData]);

  const eqOptions = useMemo(() => {
    const eqs = [
      ...new Set(
        flightData
          .map((d) => d.EQ)
          .filter(Boolean)
      ),
    ];

    return eqs
      .sort()
      .map((e) => ({
        value: e,
        label: e,
      }));
  }, [flightData]);

  const frecOptions = useMemo(() => {
    const frecs = [
      ...new Set(
        flightData
          .map((d) => d.Frecuencias)
          .filter((f) => f !== null && f !== undefined)
      ),
    ];

    return frecs
      .sort((a, b) => {
        const order = {
          DIARIO: 0,
          'L-S': 1,
          'L-V': 2,
        };

        const aOrd =
          order[a] !== undefined
            ? order[a]
            : 3 + parseInt(a || 0);

        const bOrd =
          order[b] !== undefined
            ? order[b]
            : 3 + parseInt(b || 0);

        return aOrd - bOrd;
      })
      .map((f) => ({
        value: String(f),
        label: isNaN(f)
          ? f
          : `${f} día${f === '1' ? '' : 's'}`,
      }));
  }, [flightData]);

  // ============================================================
  // FILTRADO + SORT
  // ============================================================

  const filtered = useMemo(() => {
    let data = [...flightData];

    // Buscar
    if (search) {
      const s = search.toLowerCase();

      data = data.filter((d) => {
        const id = String(d.ID || '').toLowerCase();
        const ruta = String(d.Ruta || '').toLowerCase();
        const vuelo = String(d.Vuelo || '').toLowerCase();

        return (
          id.includes(s) ||
          ruta.includes(s) ||
          vuelo.includes(s)
        );
      });
    }

    // Ruta
    if (rutaFilter) {
      data = data.filter(
        (d) => d.Ruta === rutaFilter
      );
    }

    // Equipo
    if (eqFilter) {
      data = data.filter(
        (d) => d.EQ === eqFilter
      );
    }

    // Frecuencia
    if (frecFilter) {
      data = data.filter(
        (d) => String(d.Frecuencias) === String(frecFilter)
      );
    }

    // Hora ETD
    if (horaFilter) {
      const h = parseInt(horaFilter, 10);

      data = data.filter((d) => {
        if (!d.ETD) return false;

        const etdHour = parseInt(
          String(d.ETD).split(':')[0],
          10
        );

        return etdHour === h;
      });
    }

    // Ordenamiento
    data.sort((a, b) => {
      // Agrupación por equipo
      if (groupByEq) {
        const eqA = String(a.EQ || '');
        const eqB = String(b.EQ || '');

        if (eqA < eqB) return -1;
        if (eqA > eqB) return 1;
      }

      let aVal = a[sortField];
      let bVal = b[sortField];

      if (
        sortField === 'TonsProm' ||
        sortField === 'm3Min' ||
        sortField === 'Vuelo'
      ) {
        aVal = Number(aVal || 0);
        bVal = Number(bVal || 0);
      }

      if (sortField === 'ETD') {
        aVal = String(aVal || '')
          .replace(':', '')
          .padStart(4, '0');

        bVal = String(bVal || '')
          .replace(':', '')
          .padStart(4, '0');
      }

      aVal = aVal ?? '';
      bVal = bVal ?? '';

      if (aVal < bVal) {
        return sortDir === 'asc' ? -1 : 1;
      }

      if (aVal > bVal) {
        return sortDir === 'asc' ? 1 : -1;
      }

      return 0;
    });

    return data;
  }, [
    flightData,
    search,
    rutaFilter,
    eqFilter,
    frecFilter,
    horaFilter,
    sortField,
    sortDir,
    groupByEq,
  ]);

  // ============================================================
  // PAGINACIÓN
  // ============================================================

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / perPage)
  );

  const paginatedData = filtered.slice(
    (page - 1) * perPage,
    page * perPage
  );

  // ============================================================
  // SORT HANDLER
  // ============================================================

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(
        sortDir === 'asc'
          ? 'desc'
          : 'asc'
      );
    } else {
      setSortField(field);
      setSortDir('asc');
    }

    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;

    return sortDir === 'asc'
      ? <IconArrowUp size={14} />
      : <IconArrowDown size={14} />;
  };

  // ============================================================
  // GRUPOS POR EQUIPO
  // ============================================================

  const getGroupHeader = (index) => {
    if (!groupByEq) return null;

    const current = paginatedData[index];

    const prev =
      index > 0
        ? paginatedData[index - 1]
        : null;

    if (
      !prev ||
      prev.EQ !== current.EQ
    ) {
      const count = filtered.filter(
        (d) => d.EQ === current.EQ
      ).length;

      return {
        eq: current.EQ,
        count,
      };
    }

    return null;
  };

  const eqColors = {
    '789': 'violet',
    '772': 'grape',
    '7M9': 'blue',
    '7M8': 'cyan',
    '738': 'teal',
    E90: 'orange',
  };

  // ============================================================
  // AGRUPAMIENTO POR RUTA+VUELO (colapsable)
  // ============================================================

  const groupedByVuelo = useMemo(() => {
    if (!groupByVuelo) return null;
    const map = new Map();
    filtered.forEach((row) => {
      const key = `${row.Ruta}${row.Vuelo}`;
      if (!map.has(key)) map.set(key, { key, ruta: row.Ruta, vuelo: row.Vuelo, items: [] });
      map.get(key).items.push(row);
    });
    return [...map.values()];
  }, [filtered, groupByVuelo]);

  const toggleGroupExpand = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ============================================================
  // EXPORTAR EXCEL
  // ============================================================

  const exportToExcel = () => {
    const exportData = filtered.map((row) => ({
      ID: row.ID,
      Ruta: row.Ruta,
      Vuelo: row.Vuelo,
      Equipo: row.EQ,
      Horario: row.ETD,
      Frecuencia: row.Frecuencias,
      'Tons Prom': row.TonsProm,
      'm³ Min': row.m3Min,
    }));

    const ws =
      XLSX.utils.json_to_sheet(exportData);

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      'CAP TD'
    );

    XLSX.writeFile(
      wb,
      `CAP_TD_${anio}_${String(mes).padStart(2, '0')}.xlsx`
    );
  };

  // ============================================================
  // CAMBIO DE MES
  // ============================================================

  const handleMonthChange = (value) => {
    if (!value) return;

    setMes(parseInt(value, 10));
    setPage(1);
  };

  // ============================================================
  // CAMBIO DE AÑO
  // ============================================================

  const handleYearChange = (value) => {
    if (!value) return;

    setAnio(parseInt(value, 10));
    setPage(1);
  };

  const yearOptions = useMemo(() => {
    const currentYear =
      new Date().getFullYear();

    return Array.from(
      { length: 7 },
      (_, i) => {
        const year =
          currentYear - 3 + i;

        return {
          value: String(year),
          label: String(year),
        };
      }
    );
  }, []);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      {/* ======================================================
          HEADER
      ====================================================== */}

      <Flex
        justify="space-between"
        align="center"
        mb="md"
        wrap="wrap"
        gap="md"
      >
        <Text
          fw={700}
          size="xl"
          c="#020C41"
        >
          CAP TD — Tabla Dinámica
        </Text>

        {/* ================================================
            SELECTOR MES / AÑO
            ================================================ */}

        <Group
          gap="xs"
          align="center"
        >
          {/* Mes anterior */}

          <ActionIcon
            size="sm"
            variant="light"
            color="#030D75"
            onClick={prevMonth}
            title="Mes anterior"
          >
            <IconChevronLeft size={14} />
          </ActionIcon>

          {/* Mes */}

          <Select
            value={String(mes)}
            onChange={handleMonthChange}
            data={MESES}
            size="xs"
            allowDeselect={false}
            style={{
              width: 135,
            }}
          />

          {/* Año */}

          <Select
            value={String(anio)}
            onChange={handleYearChange}
            data={yearOptions}
            size="xs"
            allowDeselect={false}
            style={{
              width: 90,
            }}
          />

          {/* Mes siguiente */}

          <ActionIcon
            size="sm"
            variant="light"
            color="#030D75"
            onClick={nextMonth}
            title="Mes siguiente"
          >
            <IconChevronRight size={14} />
          </ActionIcon>
        </Group>

        <Text
          size="xs"
          c="dimmed"
          fs="italic"
        >
          Última carga: {LAST_UPLOAD}
        </Text>
      </Flex>

      {/* ======================================================
          FILTROS
      ====================================================== */}

      <Paper
        shadow="xs"
        p="md"
        mb="md"
        radius="md"
      >
        <Flex
          gap="md"
          align="end"
          wrap="wrap"
        >
          {/* Buscar */}

          <TextInput
            label="Buscar"
            placeholder="ID, Ruta o Vuelo..."
            leftSection={
              <IconSearch size={16} />
            }
            value={search}
            onChange={(e) => {
              setSearch(
                e.currentTarget.value
              );
              setPage(1);
            }}
            style={{
              flex: 1,
              minWidth: 180,
            }}
          />

          {/* Ruta */}

          <Select
            label="Ruta"
            placeholder="Todas"
            data={rutaOptions}
            value={rutaFilter}
            onChange={(val) => {
              setRutaFilter(val);
              setPage(1);
            }}
            clearable
            searchable
            leftSection={
              <IconFilter size={16} />
            }
            style={{
              minWidth: 160,
            }}
          />

          {/* Equipo */}

          <Select
            label="Equipo"
            placeholder="Todos"
            data={eqOptions}
            value={eqFilter}
            onChange={(val) => {
              setEqFilter(val);
              setPage(1);
            }}
            clearable
            leftSection={
              <IconPlane size={16} />
            }
            style={{
              minWidth: 120,
            }}
          />

          {/* Frecuencia */}

          <Select
            label="Frecuencia"
            placeholder="Todas"
            data={frecOptions}
            value={frecFilter}
            onChange={(val) => {
              setFrecFilter(val);
              setPage(1);
            }}
            clearable
            style={{
              minWidth: 130,
            }}
          />

          {/* Horario */}

          <Select
            label="Horario (ETD)"
            placeholder="Todas las horas"
            data={horaOptions}
            value={horaFilter}
            onChange={(val) => {
              setHoraFilter(val);
              setPage(1);
            }}
            clearable
            searchable
            leftSection={
              <IconClock size={16} />
            }
            style={{
              minWidth: 160,
            }}
          />

          {/* Agrupar */}

          <Switch
            label="Agrupar EQ"
            checked={groupByEq}
            onChange={(e) => {
              setGroupByEq(e.currentTarget.checked);
              if (e.currentTarget.checked) setGroupByVuelo(false);
            }}
            color="#FA0073"
            size="sm"
            style={{
              marginBottom: 4,
            }}
          />

          <Switch
            label="Agrupar Vuelo"
            checked={groupByVuelo}
            onChange={(e) => {
              setGroupByVuelo(e.currentTarget.checked);
              if (e.currentTarget.checked) { setGroupByEq(false); setExpandedGroups(new Set()); }
            }}
            color="#030D75"
            size="sm"
            style={{
              marginBottom: 4,
            }}
          />

          {/* Excel */}

          <Button
            size="sm"
            variant="light"
            color="#FA0073"
            leftSection={
              <IconDownload size={16} />
            }
            onClick={exportToExcel}
          >
            Excel
          </Button>
        </Flex>
      </Paper>

      {/* ======================================================
          TABLA
      ====================================================== */}

      <Paper
        shadow="xs"
        radius="md"
        style={{
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            overflowX: 'auto',
          }}
        >
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="sm"
            verticalSpacing="xs"
            layout="auto"
            style={{
              textAlign: 'center',
            }}
          >
            <Table.Thead
              style={{
                backgroundColor: '#030D75',
              }}
            >
              <Table.Tr>
                {[
                  {
                    key: 'ID',
                    label: 'ID',
                  },
                  {
                    key: 'Ruta',
                    label: 'Ruta',
                  },
                  {
                    key: 'Vuelo',
                    label: 'Vuelo',
                  },
                  {
                    key: 'EQ',
                    label: 'Equipo',
                  },
                  {
                    key: 'ETD',
                    label: 'Horario',
                  },
                  {
                    key: 'Frecuencias',
                    label: 'Frecuencia',
                  },
                  {
                    key: 'TonsProm',
                    label: 'Tons Prom',
                  },
                  {
                    key: 'm3Min',
                    label: 'm³ Min',
                  },
                ].map((col) => (
                  <Table.Th
                    key={col.key}
                    style={{
                      color: 'white',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}
                    onClick={() =>
                      handleSort(
                        col.key
                      )
                    }
                  >
                    <Flex
                      gap={4}
                      justify="center"
                      align="center"
                    >
                      {col.label}

                      <SortIcon
                        field={
                          col.key
                        }
                      />
                    </Flex>
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={8}
                    style={{
                      textAlign:
                        'center',
                      padding: 30,
                    }}
                  >
                    <Text
                      size="sm"
                      c="dimmed"
                    >
                      Cargando información...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : paginatedData.length === 0 ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={8}
                    style={{
                      textAlign:
                        'center',
                      padding: 30,
                    }}
                  >
                    <Text
                      size="sm"
                      c="dimmed"
                    >
                      No hay información para{' '}
                      {getMonthName(
                        mes
                      ).toUpperCase()}{' '}
                      {anio}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : groupByVuelo && groupedByVuelo ? (
                groupedByVuelo.map((group) => {
                  const isExpanded = expandedGroups.has(group.key);
                  return (
                    <React.Fragment key={group.key}>
                      {/* Fila agrupada */}
                      <Table.Tr
                        style={{ backgroundColor: 'rgba(3,13,117,0.06)', cursor: 'pointer' }}
                        onClick={() => toggleGroupExpand(group.key)}
                      >
                        <Table.Td colSpan={2} style={{ textAlign: 'left', padding: '8px 12px' }}>
                          <Flex align="center" gap="xs">
                            {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                            <Text size="sm" fw={700} c="#020C41">{group.key}</Text>
                            <Badge color="am-blue" variant="light" size="xs">{group.ruta}</Badge>
                          </Flex>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}><Text size="sm" fw={600}>{group.vuelo}</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Badge size="xs" color="gray" variant="light">{group.items.length} equipo{group.items.length !== 1 ? 's' : ''}</Badge>
                        </Table.Td>
                        <Table.Td colSpan={4} />
                      </Table.Tr>
                      {/* Filas hijas (expandidas) */}
                      {isExpanded && group.items.map((row) => (
                        <Table.Tr key={row.ID} style={{ backgroundColor: 'rgba(3,13,117,0.02)' }}>
                          <Table.Td style={{ textAlign: 'center', paddingLeft: 24 }}><Text size="sm" fw={500} c="#020C41">{row.ID}</Text></Table.Td>
                          <Table.Td style={{ textAlign: 'center' }}><Badge variant="light" color={String(row.Ruta || '').startsWith('MEX') ? 'am-blue' : 'am-pink'} size="sm">{row.Ruta}</Badge></Table.Td>
                          <Table.Td style={{ textAlign: 'center' }}><Text size="sm">{row.Vuelo}</Text></Table.Td>
                          <Table.Td style={{ textAlign: 'center' }}><Badge variant="light" color={eqColors[row.EQ] || 'gray'} size="sm">{row.EQ || '—'}</Badge></Table.Td>
                          <Table.Td style={{ textAlign: 'center' }}><Text size="sm">{row.ETD || '—'}</Text></Table.Td>
                          <Table.Td style={{ textAlign: 'center' }}><Badge variant="dot" color={row.Frecuencias === 'DIARIO' ? 'green' : 'blue'} size="sm">{row.Frecuencias || '—'}</Badge></Table.Td>
                          <Table.Td style={{ textAlign: 'center' }}><Text size="sm" fw={600} c={Number(row.TonsProm || 0) >= 10 ? '#FA0073' : '#020C41'}>{row.TonsProm ?? '—'}</Text></Table.Td>
                          <Table.Td style={{ textAlign: 'center' }}><Text size="sm">{row.m3Min ?? '—'}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </React.Fragment>
                  );
                })
              ) : (
                paginatedData.map(
                  (row, idx) => {
                    const group =
                      getGroupHeader(
                        idx
                      );

                    return (
                      <>
                        {/* Grupo */}

                        {group && (
                          <Table.Tr
                            key={`group-${group.eq}`}
                            style={{
                              backgroundColor:
                                'rgba(3,13,117,0.06)',
                            }}
                          >
                            <Table.Td
                              colSpan={8}
                              style={{
                                textAlign:
                                  'left',
                                padding:
                                  '6px 12px',
                              }}
                            >
                              <Flex
                                align="center"
                                gap="xs"
                              >
                                <Badge
                                  color={
                                    eqColors[
                                      group.eq
                                    ] ||
                                    'gray'
                                  }
                                  variant="filled"
                                  size="sm"
                                >
                                  {group.eq}
                                </Badge>

                                <Text
                                  size="xs"
                                  fw={600}
                                  c="#020C41"
                                >
                                  {
                                    group.count
                                  }{' '}
                                  vuelo
                                  {group.count !==
                                  1
                                    ? 's'
                                    : ''}
                                </Text>
                              </Flex>
                            </Table.Td>
                          </Table.Tr>
                        )}

                        {/* Registro */}

                        <Table.Tr
                          key={row.ID}
                        >
                          {/* ID */}

                          <Table.Td
                            style={{
                              textAlign:
                                'center',
                            }}
                          >
                            <Text
                              size="sm"
                              fw={500}
                              c="#020C41"
                            >
                              {row.ID}
                            </Text>
                          </Table.Td>

                          {/* Ruta */}

                          <Table.Td
                            style={{
                              textAlign:
                                'center',
                            }}
                          >
                            <Badge
                              variant="light"
                              color={
                                String(
                                  row.Ruta ||
                                    ''
                                ).startsWith(
                                  'MEX'
                                )
                                  ? 'am-blue'
                                  : 'am-pink'
                              }
                              size="sm"
                            >
                              {row.Ruta}
                            </Badge>
                          </Table.Td>

                          {/* Vuelo */}

                          <Table.Td
                            style={{
                              textAlign:
                                'center',
                            }}
                          >
                            <Text size="sm">
                              {row.Vuelo}
                            </Text>
                          </Table.Td>

                          {/* Equipo */}

                          <Table.Td
                            style={{
                              textAlign:
                                'center',
                            }}
                          >
                            <Badge
                              variant="light"
                              color={
                                eqColors[
                                  row.EQ
                                ] ||
                                'gray'
                              }
                              size="sm"
                            >
                              {row.EQ ||
                                '—'}
                            </Badge>
                          </Table.Td>

                          {/* Horario */}

                          <Table.Td
                            style={{
                              textAlign:
                                'center',
                            }}
                          >
                            <Text size="sm">
                              {row.ETD ||
                                '—'}
                            </Text>
                          </Table.Td>

                          {/* Frecuencia */}

                          <Table.Td
                            style={{
                              textAlign:
                                'center',
                            }}
                          >
                            <Badge
                              variant="dot"
                              color={
                                row.Frecuencias ===
                                'DIARIO'
                                  ? 'green'
                                  : row.Frecuencias ===
                                      'L-V' ||
                                    row.Frecuencias ===
                                      'L-S'
                                  ? 'blue'
                                  : parseInt(
                                      row.Frecuencias ||
                                        0
                                    ) <= 3
                                  ? 'red'
                                  : 'orange'
                              }
                              size="sm"
                            >
                              {row.Frecuencias ||
                                '—'}
                            </Badge>
                          </Table.Td>

                          {/* Tons */}

                          <Table.Td
                            style={{
                              textAlign:
                                'center',
                            }}
                          >
                            <Text
                              size="sm"
                              fw={600}
                              c={
                                Number(
                                  row.TonsProm ||
                                    0
                                ) >= 10
                                  ? '#FA0073'
                                  : '#020C41'
                              }
                            >
                              {row.TonsProm ??
                                '—'}
                            </Text>
                          </Table.Td>

                          {/* M3 */}

                          <Table.Td
                            style={{
                              textAlign:
                                'center',
                            }}
                          >
                            <Text size="sm">
                              {row.m3Min ??
                                '—'}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      </>
                    );
                  }
                )
              )}
            </Table.Tbody>
          </Table>
        </Box>

        {/* ====================================================
            PAGINACIÓN
            ==================================================== */}

        <Flex
          justify="space-between"
          align="center"
          p="md"
        >
          <Text
            size="sm"
            c="dimmed"
          >
            {filtered.length === 0
              ? '0 registros'
              : `Mostrando ${
                  (page - 1) *
                    perPage +
                  1
                }–${Math.min(
                  page * perPage,
                  filtered.length
                )} de ${
                  filtered.length
                }`}
          </Text>

          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            color="am-blue"
            size="sm"
          />
        </Flex>
      </Paper>
    </>
  );
}